import React, { FC, useCallback, useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { isMedia } from '@shared/lib/html';
import { Label } from '@shared/ui/label';

import { STATE_TO_STATUS_MAP } from '../../config';
import { hasVideoTracks } from '../../lib/media';
import { signalQualityIcon } from '../../lib/misc';
import { CallControls } from '../call-controls';
import { ConferencePanel } from '../conference-panel';
import { EMPTY_CAPTIONS_SRC, RemoteMediaStage } from '../remote-media-stage';
import { TransferPanel } from '../transfer-panel';
import type { ICallFrameProps } from './types';

import styles from './styles.module.scss';

export const CallFrame: FC<ICallFrameProps> = ({
	currentCall,
	callTimer,
	isLoadingRequest,
	localVideoRef,
	remoteVideoRef,
	signalQuality,
	onCreateConference,
	onParkingCall,
	onTransferCall,
	...rest
}) => {
	const [isOpenTransferPanel, setIsOpenTransferPanel] = useState(false);
	const [isOpenConferencePanel, setIsOpenConferencePanel] = useState(false);
	const [isControlsVisible, setIsControlsVisible] = useState<boolean>(() => isMedia('sm'));
	const [isLocalVideoExpanded, setIsLocalVideoExpanded] = useState(false);
	const hideControlsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const defaultOpenKeysRef = useRef<boolean>(true);
	const isMobile = isMedia('sm');
	const { displayName, aor, state, remote: remoteMedia } = currentCall;
	const contactName = displayName || aor;

	// Производные флаги для UI состояний
	const callStatus = STATE_TO_STATUS_MAP[state];
	const isLocalHold = state === 'hold';
	const isRemoteMute = !remoteMedia?.audio;
	// Базовый флаг video в сигналке + проверка, что реально есть живые видеотреки в плеере
	const isRemoteVideo = !!remoteMedia?.video;
	const isRemoteVideoActive = isRemoteVideo && hasVideoTracks(remoteVideoRef);
	const isLocalVideo = hasVideoTracks(localVideoRef);

	const { signalIcon, signalHint } = signalQualityIcon(signalQuality);
	const clearHideControlsTimer = useCallback(() => {
		if (hideControlsTimerRef.current) {
			clearTimeout(hideControlsTimerRef.current);
			hideControlsTimerRef.current = null;
		}
	}, []);

	const scheduleHideControls = useCallback(() => {
		if (isMobile) return;

		clearHideControlsTimer();
		hideControlsTimerRef.current = setTimeout(() => {
			setIsControlsVisible(false);
			hideControlsTimerRef.current = null;
		}, 5000);
	}, [clearHideControlsTimer, isMobile]);

	const showControls = useCallback(() => {
		setIsControlsVisible(true);
		scheduleHideControls();
	}, [scheduleHideControls]);

	useEffect(
		() => () => {
			clearHideControlsTimer();
		},
		[clearHideControlsTimer]
	);

	useEffect(() => {
		if (isMobile) {
			setIsControlsVisible(true);
			clearHideControlsTimer();
		}
	}, [clearHideControlsTimer, isMobile]);

	const handleOpenTransferPanel = () => {
		setIsOpenTransferPanel(true);
	};

	const handleOpenConferencePanel = (isOpenDialPad: boolean) => {
		defaultOpenKeysRef.current = isOpenDialPad;
		setIsOpenConferencePanel((prev) => !prev);
	};

	const handleOpenParkingPanel = () => {
		onParkingCall();
	};

	const handleCloseTransferPanel = () => {
		setIsOpenTransferPanel(false);
	};

	const handleCloseConferencePanel = () => {
		setIsOpenConferencePanel(false);
	};

	const toggleLocalVideoSize = () => {
		setIsLocalVideoExpanded((prev) => !prev);
	};

	const isDisabledCallStatus = ['hold', 'parked', 'remoteHold'].includes(currentCall.state);
	const isEndedCallStatus = ['accepted', 'declined'].includes(currentCall.state);
	const shouldShowControls = isMobile || isControlsVisible;

	return (
		<div className={styles.Container}>
			<div
				className={cn(styles.CallFrameContainer, {
					[styles.hide]: isOpenTransferPanel || isOpenConferencePanel,
				})}
				onMouseEnter={showControls}
				onMouseLeave={scheduleHideControls}
				onMouseMove={showControls}
			>
				{/* Превью локального видео; скрываем если треков нет */}
				<div
					className={cn(styles.LocalVideoContainer, {
						[styles.Hide]: !isLocalVideo,
						[styles.LocalVideoContainerExpanded]: isLocalVideoExpanded,
					})}
					onClick={toggleLocalVideoSize}
				>
					<video ref={localVideoRef} autoPlay muted playsInline className={styles.VideoBlock}>
						<track kind="captions" label="Local preview" src={EMPTY_CAPTIONS_SRC} />
					</video>
				</div>

				<RemoteMediaStage
					callStatus={callStatus}
					contactName={contactName}
					isDisabledCallStatus={isDisabledCallStatus}
					isRemoteMute={isRemoteMute}
					isRemoteVideoActive={isRemoteVideoActive}
					remoteVideoRef={remoteVideoRef}
					state={state}
				/>

				{!isEndedCallStatus && (
					/* Таймер + кнопки управления вызовом */
					<div className={styles.ControlsWrapper}>
						<Label
							color="white"
							tagClass={cn(styles.LabelTimer, {
								[styles.LabelTimerShifted]: !shouldShowControls,
							})}
							text={
								<div className={styles.LabelInfo}>
									{!isLocalHold && (
										<span
											className={styles.LabelIcon}
											dangerouslySetInnerHTML={{ __html: signalIcon }}
											title={signalHint}
										/>
									)}
									{callTimer}
								</div>
							}
						/>
						<div
							className={cn(styles.Controls, {
								[styles.ControlsVisible]: shouldShowControls,
							})}
						>
							<CallControls
								currentCall={currentCall}
								isLoadingRequest={isLoadingRequest}
								isOpenConferencePanel={isOpenConferencePanel}
								onCreateConferenceClick={handleOpenConferencePanel}
								onParkingCallClick={handleOpenParkingPanel}
								onTransferClick={handleOpenTransferPanel}
								{...rest}
							/>
						</div>
					</div>
				)}
			</div>

			{/* --- Панель «Перевести звонок» --- */}
			<TransferPanel
				disabledCall={isLoadingRequest}
				isOpened={isOpenTransferPanel}
				onClose={handleCloseTransferPanel}
				onRefer={onTransferCall}
			/>

			{/* --- Панель «Конференции» --- */}
			<ConferencePanel
				defaultOpenKeys={defaultOpenKeysRef.current}
				disabledCall={isLoadingRequest}
				isOpened={isOpenConferencePanel}
				onClose={handleCloseConferencePanel}
				onCreateConference={onCreateConference}
			/>
		</div>
	);
};
